import React, { useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import * as FiIcons from 'react-icons/fi';

const RichText = ({ content, className = '' }) => {

    // Process content string: inject icons directly into the HTML string before rendering.
    // This avoids the "flash and disappear" issue caused by React re-renders wiping out DOM modifications.
    const processedContent = useMemo(() => {
        if (!content) return '';

        // Improved regex to identify span tags with data-tiptap-icon or data-icon
        // Captures:
        // 1: Everything before the icon attribute
        // 2: The attribute name itself (data-tiptap-icon or data-icon)
        // 3: The icon name
        // 4: Everything after the icon attribute up to closing >
        // 5: The inner content
        // 6: The closing tag
        return content.replace(/(<span[^>]*\s)(data-(?:tiptap-)?icon)=["']([^"']+)["']([^>]*)>(.*?)<\/span>/gi, (match, beforeAttr, attrName, iconName, afterAttr, innerContent) => {

            // Resolve icon component
            let IconComponent = FiIcons[iconName];

            // Case-insensitive fallback
            if (!IconComponent && iconName) {
                const lower = iconName.toLowerCase();
                const key = Object.keys(FiIcons).find(k => k.toLowerCase() === lower);
                if (key) IconComponent = FiIcons[key];
            }

            if (!IconComponent) {
                return match; // Return original if not found
            }

            try {
                // Generate SVG string
                const svgString = renderToStaticMarkup(<IconComponent />);

                // Reconstruct the tag with existing attributes + forced styles
                // We add style="display:inline-flex;..." to ensure visibility

                // Check if style attribute exists in Before or After parts
                let styles = "display: inline-flex; align-items: center; justify-content: center; vertical-align: middle; margin: 0 0.1em; line-height: 1; position: relative;";

                const styleRegex = /style=["']([^"']*)["']/i;
                const existingStyleMatch = (beforeAttr + afterAttr).match(styleRegex);

                if (existingStyleMatch) {
                    // Start new style with existing ones
                    // We remove duplicate style definition later
                    styles = existingStyleMatch[1] + (existingStyleMatch[1].trim().endsWith(';') ? ' ' : '; ') + styles;
                }

                // Clean old style attributes from parts to avoid duplicates
                const cleanBefore = beforeAttr.replace(styleRegex, '');
                const cleanAfter = afterAttr.replace(styleRegex, '');

                const finalBefore = cleanBefore.trim();
                const finalAfter = cleanAfter.trim();

                // Rebuild tag
                // cleanBefore already acts as the tag start e.g. "<span "
                // We just append attributes

                // If cleanBefore ends with the tag name "span" or "span ", we are good.
                // It starts with "<span".

                // We construct: 
                // <span {attributes} style="...">...</span>

                // But wait, cleanBefore includes "<span".
                // So we just return:
                // ${cleanBefore} ${attrName}="${iconName}" ${finalAfter} style="${styles}">${svgString}</span>

                // Ensure spaces
                return `${finalBefore} ${attrName}="${iconName}" ${finalAfter} style="${styles}">${svgString}</span>`;

            } catch (e) {
                console.error("RichText processing error:", e);
                return match;
            }
        });
    }, [content]);

    return (
        <div
            className={`block-text ${className} rich-text-content`}
            dangerouslySetInnerHTML={{ __html: processedContent }}
        />
    );
};

export default RichText;
